import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto'; // Import Chart.js for the chart
import { FaWeightHanging } from 'react-icons/fa'; // Import weight icon from react-icons
import { useTranslation } from 'react-i18next'; // Import translation hook
import './../../style/WeightHistoryPage.css'; // Import the CSS file

const WeightHistoryPage = () => {
    const { id } = useParams();
    const location = useLocation();
    const { t } = useTranslation(); // Translation function
    const initialWeightData = location.state?.weightData || []; // Safely access weightData
    const [weightData, setWeightData] = useState(initialWeightData);
    const [newWeight, setNewWeight] = useState({
        date: new Date().toISOString().split('T')[0], // Default to today's date
        weightKg: '',
    });
    const [showForm, setShowForm] = useState(false); // Toggle visibility of the input form
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    const handleAddWeight = async () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/weight-history`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(newWeight),
            });

            if (!response.ok) {
                throw new Error(t('weightHistoryPage.addFailed')); // Use translation for error
            }

            const updatedWeightHistory = await response.json(); // Backend returns the weight entries directly
            setWeightData(updatedWeightHistory.sort((a, b) => new Date(b.date) - new Date(a.date))); // Sort by most recent date
            setNewWeight({ date: new Date().toISOString().split('T')[0], weightKg: '' }); // Reset the form
            setShowForm(false); // Hide the form after adding
        } catch (error) {
            console.error(error.message);
        }
    };

    const chartOptions = {
        plugins: {
            legend: {
                display: false, // Disable the legend
            },
        },
    };

    const chartData = {
        labels: weightData
            .slice() // Create a copy of the array to avoid mutating the original
            .sort((a, b) => new Date(a.date) - new Date(b.date)) // Sort by date in ascending order
            .map((entry) => new Date(entry.date).toLocaleDateString()),
        datasets: [
            {
                label: t('weightHistoryPage.chartLabel'), // Use translation for chart label
                data: weightData
                    .slice() // Create a copy of the array
                    .sort((a, b) => new Date(a.date) - new Date(b.date)) // Sort by date in ascending order
                    .map((entry) => entry.weightKg),
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                fill: true,
            },
        ],
    };

    return (
        <div className="weight-history-page bg-dafauna-background rounded-xl shadow-md p-8">
            <div className="page-title flex items-center justify-center gap-2 mb-4">
                <FaWeightHanging className="weight-icon text-dafauna-primary text-2xl" />
                <h1 className="text-dafauna-darkText text-2xl font-bold">{t('weightHistoryPage.title')}</h1>
            </div>
            <div className="chart-container mb-6">
                {weightData.length > 0 ? (
                    <Line data={chartData} options={chartOptions} />
                ) : (
                    <p className="no-data-message text-dafauna-gray text-center">{t('weightHistoryPage.noData')}</p>
                )}
            </div>

            <button
                onClick={() => setShowForm(!showForm)}
                className="btn update-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
            >
                {showForm ? t('weightHistoryPage.cancel') : t('weightHistoryPage.update')}
            </button>

            {showForm && (
                <div className="add-weight-form bg-white border border-gray-300 rounded-md p-4 mt-4">
                    <input
                        type="date"
                        value={newWeight.date}
                        onChange={(e) => setNewWeight({ ...newWeight, date: e.target.value })}
                        required
                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 mb-4 focus:outline-none focus:border-dafauna-primary"
                    />
                    <input
                        type="number"
                        placeholder={t('weightHistoryPage.weightPlaceholder')}
                        value={newWeight.weightKg}
                        onChange={(e) => setNewWeight({ ...newWeight, weightKg: e.target.value })}
                        required
                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 mb-4 focus:outline-none focus:border-dafauna-primary"
                    />
                    <button
                        onClick={handleAddWeight}
                        className="btn add-weight-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                    >
                        {t('weightHistoryPage.addButton')}
                    </button>
                </div>
            )}

            <div className="weight-table mt-6">
                {weightData.length > 0 ? (
                    <table className="weight-table">
                        <thead>
                            <tr className="bg-dafauna-primary text-white">
                                <th className="px-4 py-2">{t('weightHistoryPage.date')}</th>
                                <th className="px-4 py-2">{t('weightHistoryPage.weight')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {weightData.map((entry, index) => (
                                <tr key={index} className="hover:bg-gray-100">
                                    <td className="px-4 py-2 text-center">{new Date(entry.date).toLocaleDateString()}</td>
                                    <td className="px-4 py-2 text-center">{entry.weightKg}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="no-data-message text-dafauna-gray text-center">{t('weightHistoryPage.noEntries')}</p>
                )}
            </div>
        </div>
    );
};

export default WeightHistoryPage;